import { chartTheme } from "./chartTheme";

/**
 * HourlySourceMix (UX-006 §5.13) — stacked bars per hour on the source palette.
 * Sources: solar, grid import, storage discharge, diesel.
 */

export interface HourlySourceMixProps {
  hours: string[];
  sources: { name: string; values: number[]; label?: string }[];
  unit: string;
  height?: number;
  /** Optional demand line drawn over the bars on the same unit (e.g. load). */
  load?: number[];
  loadLabel?: string;
}

export function HourlySourceMix({ hours, sources, unit, height = 180, load, loadLabel = "load" }: HourlySourceMixProps) {
  const theme = chartTheme();
  const count = hours.length;
  if (count === 0) return null;

  const totals = hours.map((_, hi) =>
    sources.reduce((sum, s) => sum + (s.values[hi] ?? 0), 0),
  );
  const maxVal = Math.max(1, ...totals, ...(load ?? []));

  const padL = 44;
  const padR = 8;
  const padT = 8;
  const padB = 24;
  const plotW = 600 - padL - padR;
  const plotH = height - padT - padB;
  const slotW = plotW / count;
  const barW = slotW * 0.7;

  const yOf = (v: number) => padT + plotH - (v / maxVal) * plotH;

  const gridLines = [];
  const gridCount = 4;
  for (let i = 0; i <= gridCount; i++) {
    const val = Math.round((maxVal / gridCount) * i);
    gridLines.push({ y: yOf(val), label: val.toString() });
  }

  return (
    <figure role="group" aria-label={`Hourly source mix (${unit})`} className="m-0">
      <svg
        role="img"
        aria-label={`Hourly source mix (${unit})`}
        viewBox={`0 0 600 ${height}`}
        style={{ width: "100%", height }}
      >
        {/* Gridlines */}
        {gridLines.map((g) => (
          <g key={g.label}>
            <line x1={padL} y1={g.y} x2={600 - padR} y2={g.y} stroke={theme.grid} strokeWidth={1} />
            <text x={padL - 4} y={g.y + 3} textAnchor="end" fontSize={9.5} fill={theme.textMuted}>
              {g.label}
            </text>
          </g>
        ))}

        {/* X axis */}
        <line x1={padL} y1={padT + plotH} x2={600 - padR} y2={padT + plotH} stroke={theme.axis} strokeWidth={1} />

        {/* Stacked bars */}
        {hours.map((lbl, hi) => {
          const x = padL + hi * slotW + (slotW - barW) / 2;
          let cumY = padT + plotH;

          return (
            <g key={lbl}>
              {sources.map((src) => {
                const v = src.values[hi] ?? 0;
                if (v <= 0) return null;
                const barH = (v / maxVal) * plotH;
                cumY -= barH;
                return (
                  <rect
                    key={src.name}
                    x={x}
                    y={cumY}
                    width={barW}
                    height={barH}
                    fill={theme.source[src.name] ?? theme.bar}
                  />
                );
              })}
              {/* X label — show every other for density */}
              {hi % 2 === 0 && (
                <text
                  x={padL + hi * slotW + slotW / 2}
                  y={padT + plotH + 14}
                  textAnchor="middle"
                  fontSize={9.5}
                  fill={theme.textMuted}
                >
                  {lbl}
                </text>
              )}
            </g>
          );
        })}

        {/* Demand line over the bars (same unit) */}
        {load && load.length > 0 && (
          <polyline
            fill="none"
            stroke={theme.source.load}
            strokeWidth={2}
            points={load
              .map((v, hi) => `${padL + hi * slotW + slotW / 2},${yOf(v)}`)
              .join(" ")}
          />
        )}
      </svg>

      {/* Legend */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: "0.72rem", color: theme.textMuted, paddingTop: 4 }}>
        {sources.map((s) => (
          <span key={s.name} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: 2,
                backgroundColor: theme.source[s.name] ?? theme.bar,
              }}
            />
            {s.label ?? s.name}
          </span>
        ))}
        {load && load.length > 0 && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span aria-hidden style={{ display: "inline-block", width: 14, height: 0, borderTop: `2px solid ${theme.source.load}` }} />
            {loadLabel}
          </span>
        )}
      </div>

      <figcaption className="sr-only">
        Hourly source mix: {sources.map((s) => s.name).join(", ")} over {count} hours ({unit}).
      </figcaption>
    </figure>
  );
}
